import { useMutation, useQuery } from "@tanstack/react-query";

import {
  createEnrollment,
  createPaymentOrder,
  fetchCohort,
  fetchCohorts,
  fetchFaqs,
  joinWaitlist,
  openRazorpayCheckout,
  requestCallback,
  verifyPayment,
} from "./client";
import { googleSignIn, getCurrentUser } from "./auth";

export const useCohorts = () =>
  useQuery({ queryKey: ["reinforce", "cohorts"], queryFn: fetchCohorts, retry: 1 });

export const useCohort = (slug) =>
  useQuery({ queryKey: ["reinforce", "cohort", slug], queryFn: () => fetchCohort(slug), enabled: Boolean(slug) });

export const useFaqs = () => useQuery({ queryKey: ["reinforce", "faqs"], queryFn: fetchFaqs });

export const useJoinWaitlist = () => useMutation({ mutationFn: joinWaitlist });

export const useRequestCallback = () => useMutation({ mutationFn: requestCallback });

export const useGoogleSignIn = () => useMutation({ mutationFn: googleSignIn });

export { getCurrentUser };

// Joining a cohort, once signed in: create the enrolment, get its payment
// order, then verify. Real Razorpay checkout sits between the last two steps —
// see ../INTEGRATION_CHECKLIST.md.
export const useCheckout = () =>
  useMutation({
    mutationFn: async ({ cohortSlug, cohortTitle, user }) => {
      // 1. Create enrollment
      const enrollment = await createEnrollment({ cohortSlug, name: user?.name, email: user?.email });

      // 2. Create Razorpay order
      const order = await createPaymentOrder({
        enrollmentId: enrollment.id,
        amount: enrollment.amount_paid,
        currency: "INR",
      });

      // 3. Open Razorpay checkout and wait for payment
      const razorpayResult = await openRazorpayCheckout({
        orderId: order.order_id,
        amount: order.amount,
        currency: order.currency,
        keyId: order.key_id,
        userEmail: user?.email,
        userName: user?.name,
        cohortTitle,
      });

      // 4. Verify payment with backend
      const payment = await verifyPayment({
        razorpayOrderId: razorpayResult.razorpay_order_id,
        razorpayPaymentId: razorpayResult.razorpay_payment_id,
        razorpaySignature: razorpayResult.razorpay_signature,
      });

      return { enrollment, order, payment };
    },
  });
